import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useState,
} from 'react';

import {
  Conversation,
  ConversationContext,
  InProgressResponse,
} from '@stripe-internal/conversations-client';
import {
  ViewMode,
  useWidget,
  Visibility,
} from '@stripe-internal/conversations-ui';
import {defer} from '@sail/utils';
import {
  Config,
  Profile,
} from '@stripe-internal/conversations-shared-dependencies';
import {Topic} from '../components/TopicSelector';
import {Title} from '../components/Title';
import {devLog} from '../helpers/dev';

export enum ScreenName {
  Blank = 'Blank',
  Splash = 'Splash',
  Chat = 'Chat',
  RequestACall = 'RequestACall',
  RequestACallConfirmation = 'RequestACallConfirmation',
  RequestACallConfirmationInline = 'RequestACallConfirmationInline',
  CSAT = 'CSAT',
  DotComCSAT = 'DotComCSAT',
  Error = 'Error',
  SessionRetry = 'SessionRetry',
  Test = 'Test',
}

type ScreenOptions = {
  showHeader: boolean;
  title: ReactNode;
  preferredViewMode: ViewMode;
};

const defaultScreenOptions: ScreenOptions = {
  showHeader: true,
  title: <Title />,
  preferredViewMode: 'support-widget',
};

export type Screen = Partial<ScreenOptions> &
  (
    | {
        name: ScreenName.Blank;
      }
    | {
        name: ScreenName.Splash;
      }
    | {
        name: ScreenName.Chat;
        props?: {
          conversationId?: string;
          context?: ConversationContext;
          inProgressResponse?: InProgressResponse;
        };
      }
    | {
        name: ScreenName.RequestACall;
        props: {
          topic: Topic;
          waitTime?: ReactNode;
        };
      }
    | {
        name: ScreenName.RequestACallConfirmation;
        props: {
          name: string;
          phoneNumber: string;
          waitTime?: ReactNode;
        };
      }
    | {
        name: ScreenName.RequestACallConfirmationInline;
        props: {
          name: string;
          phoneNumber: string;
          message: string;
        };
      }
    | {
        name: ScreenName.CSAT;
        showHeader: false;
        preferredViewMode: 'csat-survey';
        props: {
          csatSurveyToken: string;
          conversation: Conversation;
        };
      }
    | {
        name: ScreenName.DotComCSAT;
        props: {
          conversation: Conversation;
        };
      }
    | {
        name: ScreenName.Error;
        props?: {
          onContactClick?: () => void;
          includeSignInSuggestion?: boolean;
          showInvalidSessionMessage?: boolean;
          sentryEventId?: string;
        };
      }
    | {
        name: ScreenName.SessionRetry;
      }
    | {
        name: ScreenName.Test;
      }
  );

export type TransitionOptions = {
  // if true, opens the widget on transition
  open: boolean;

  visibility?: Visibility;

  // if true, resets the widget on transition
  reset: boolean;
};

const defaultTransitionOptions: TransitionOptions = {
  open: false,
  reset: true,
};

export interface ScreenController {
  screen: Screen;
  transitionTo: (screen: Screen, opts?: Partial<TransitionOptions>) => void;
}

export const BlankScreen: Screen = {name: ScreenName.Blank};
export const SplashScreen: Screen = {name: ScreenName.Splash};

export const ScreenContext = createContext<ScreenController>({
  screen: BlankScreen,
  transitionTo: () => {},
});

type Props = {
  initialScreen?: Screen;
  children: ReactNode;
};

export const ScreenProvider = ({
  initialScreen = BlankScreen,
  children,
}: Props) => {
  const widget = useWidget();

  const [screen, setScreen] = useState<Screen>(initialScreen);

  const transitionTo = useCallback(
    (newScreen: Screen, transitionOptions = {}) => {
      const opts = {...defaultTransitionOptions, ...transitionOptions};

      devLog('transitionTo', newScreen, opts);

      setScreen({...newScreen});

      const preferredViewMode =
        newScreen.preferredViewMode ?? defaultScreenOptions.preferredViewMode;
      widget.requestViewMode(preferredViewMode);

      const title = newScreen.title ?? defaultScreenOptions.title;
      widget.header.setTitle(title);

      const showHeader =
        newScreen.showHeader ?? defaultScreenOptions.showHeader;
      widget.header.setVisibility(showHeader);

      if (opts.reset) {
        // let each screen be responsible for configuring the widget as appropriate
        widget.reset();
      }

      // want to defer these visibility changes so that the screen is set beforehand
      if (opts.visibility === 'minimized') {
        // if minimize is passed, prefer it to open
        defer(() => widget.minimize());
      } else if (opts.visibility === 'open' || opts.open) {
        defer(() => widget.open());
      }
    },
    [widget],
  );

  return (
    <ScreenContext.Provider value={{screen, transitionTo}}>
      {children}
    </ScreenContext.Provider>
  );
};

export const useScreen = () => {
  return useContext(ScreenContext);
};

export function defaultScreenFor(
  config: Config,
  profile: Profile,
  needsAuth = false,
): Screen {
  const {audience, requiresSession} = config;
  const {visitor, featureFlags} = profile;
  const {enableUniversalHelp} = featureFlags;

  if (needsAuth) {
    return {name: ScreenName.Error, props: {includeSignInSuggestion: true}};
  }

  if (requiresSession && visitor.sessionType === 'anonymous') {
    return {name: ScreenName.SessionRetry};
  }

  if (enableUniversalHelp) {
    return {name: ScreenName.Chat, props: {context: {audience}}};
  }

  return {name: ScreenName.Splash};
}
